/**
 * La coda di orientamento: il nastro dei lease, come chip nella riga di testa.
 *
 * **Serve a sapere dove si e', non a prendere lavoro.** E' la differenza che
 * distingue questa coda da quella del pool: qui non c'e' nessun gesto che
 * acquisisca un candidato. Un clic che riservasse lavoro romperebbe il limite
 * della sessione senza che l'operatore lo abbia chiesto, e — peggio — lo
 * farebbe dal posto in cui si guarda per orientarsi, cioe' distrattamente.
 *
 * **Il nastro dei lease e' l'elemento proprio di questa schermata.** Non e' un
 * ornamento: e' l'unica figura che risponde in un'occhiata alle tre domande che
 * l'operatore si fa fra un candidato e l'altro — *quanti ne ho in mano, a che
 * punto sono, che fine hanno fatto quelli che ho gia' deciso.* Le stesse tre
 * cose scritte come numeri sarebbero tre letture; qui sono una. Ogni segmento e'
 * un lease del batch, nell'ordine in cui e' stato riservato: la posizione
 * corrente ha il suo segno, i decisi portano il glifo del loro esito, e i
 * rinviati tornano vuoti. Il colore non porta niente da solo — il glifo lo
 * ripete, e la parola sta nel nome accessibile del segmento.
 *
 * **Nella riga di testa e non in basso (GI-266).** Stava su una fascia sua
 * sotto la tela; con la pellicola dei fotogrammi (GI-265) sotto la tela erano
 * due nastri, e la tela perdeva un'altra riga. Un batch sono pochi lease —
 * cinque, dieci — e cinque chip di quaranta pixel stanno nella riga di testa
 * accanto ai conteggi, dove si legge «2 in attesa · 1 deciso» e si vede
 * *quali*. Non si chiude: e' troppo piccolo perche' valga la pena.
 */
import { useT } from '../i18n/LocaleProvider'
import type { IntentState } from '../outbox'
import { rankOf } from '../design/scales'
import { INTENT_TONES, IN_ATTESA, leaseLabel, RINVIATO } from './state'
import type { Candidate } from '../types'

export interface QueueStripProps {
  candidates: Candidate[]
  /** Dove sta lo sguardo, nell'ordine della coda. */
  at: number
  /** Che fine hanno fatto le decisioni, candidato per candidato. */
  states: Map<string, IntentState>
  /** I candidati restituiti alla coda in questa sessione. */
  deferred: Set<string>
  /** Chi tiene questa sessione: serve a dire «in mano a te» invece del nome. */
  mine: string | null
  /** Per identita' e non per posizione: fra il clic e la lettura seguente della
   *  coda la terza riga puo' non essere piu' lo stesso candidato. */
  onGo: (detectionId: string) => void
}

/** Come si nomina lo stato di un segmento: parola, glifo e tono. */
function stateOf(
  candidate: Candidate,
  states: Map<string, IntentState>,
  deferred: Set<string>,
) {
  const decided = states.get(candidate.detection_id)
  if (decided) return INTENT_TONES[decided]
  if (deferred.has(candidate.detection_id)) return RINVIATO
  return IN_ATTESA
}

export function QueueStrip({
  candidates,
  at,
  states,
  deferred,
  mine,
  onGo,
}: QueueStripProps): JSX.Element {
  const t = useT()
  return (
    <nav className="cockpit-coda" aria-label={t('cockpit.queueStrip')}>
      <h2 className="solo-lettori">{t('cockpit.queueStrip')}</h2>
      {candidates.length === 0 && (
        <p className="tenue">{t('cockpit.queueEmpty')}</p>
      )}
      <ol className="cockpit-nastro">
        {candidates.map((one, index) => {
          const tone = stateOf(one, states, deferred)
          const held = mine !== null && one.lease_token != null
          const rank = rankOf(one.level)
          // Tutto cio' che il chip non scrive lo dice al passaggio del
          // puntatore e a chi ascolta: stato, rango col significato,
          // descrizione, lease. Il chip mostra glifo e codice — risponde a
          // «dove sono», non a «che cos'e' questo», che sta nel pannello.
          const said = [
            tone.label,
            `${rank.name} · ${rank.meaning}`,
            one.description,
            leaseLabel(one.lease_state, held),
          ]
            .filter((piece) => piece !== '')
            .join(' · ')
          return (
            <li key={one.detection_id} aria-current={index === at ? 'true' : undefined}>
              <button
                type="button"
                className="cockpit-segmento"
                // Solo lo sguardo: nessuna acquisizione, nessun verdetto.
                onClick={() => onGo(one.detection_id)}
                style={{ borderBottomColor: `var(--${tone.tone})` }}
                title={said}
              >
                <span className="cockpit-segno" style={{ color: `var(--${tone.tone})` }} aria-hidden="true">
                  {tone.glyph}
                </span>
                <span className="cockpit-codice">{one.code}</span>
                <span className="solo-lettori"> · {said}</span>
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
